// ============================================================================
// STAY MEALS — a stay's meal allowances day by day (buffet check-off slice 1)
// ============================================================================
// In-House Guests → Meals. Shows, for every day of the stay, the breakfast, lunch,
// dinner and MAP allowances of each plan line (rooms on the same line share one
// allowance), what the restaurant has marked against them, and — for a MAP stay —
// the meal the guests are expected to take. That expectation is the stay preference
// (set at check-in) unless a day has its own choice; Front Office changes both here
// (pms-inhouse `meals`). It predicts the kitchen forecast and the restaurant
// follow-up; under the plan's guest's-choice rule the guest still picks on the day.
// Past days, and days whose MAP meal is used, cannot change.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { mealEntitlementService } from 'api/services/mealEntitlementService';
import type { MajorMealPref, MealAllowance, StayMealDay, StayMeals } from 'api/types/stayMeals.types';

interface Props {
  isOpen: boolean;
  bookingId: string | null;
  /** Shown in the header while loading. */
  roomNumbers?: string;
  onClose: () => void;
}

type Choice = '' | MajorMealPref;

const MEAL_LABEL: Record<string, string> = { LUNCH: 'Lunch', DINNER: 'Dinner', EITHER: 'Either', BREAKFAST: 'Breakfast' };

const fmtDay = (v: string) =>
  new Date(v).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' });
const fmtDate = (v: string) =>
  new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
const pax = (a: number, c: number) => `${a}A${c ? ` ${c}C` : ''}`;
const dayKey = (v: string) => v.slice(0, 10);

/** "2A 1C" or "used 1A of 2A 1C". */
const allowanceText = (list: MealAllowance[]) => {
  if (list.length === 0) return null;
  const a = list.reduce((s, x) => s + x.adults, 0);
  const c = list.reduce((s, x) => s + x.children, 0);
  const ua = list.reduce((s, x) => s + x.usedAdults, 0);
  const uc = list.reduce((s, x) => s + x.usedChildren, 0);
  return ua + uc > 0 ? `${pax(ua, uc)} of ${pax(a, c)}` : pax(a, c);
};

const StayMealsModal: React.FC<Props> = ({ isOpen, bookingId, roomNumbers, onClose }) => {
  const [stay, setStay] = useState<StayMeals | null>(null);
  const [loading, setLoading] = useState(false);
  const [stayPref, setStayPref] = useState<Choice>('');
  const [dayEdits, setDayEdits] = useState<Record<string, Choice>>({});
  const [remarks, setRemarks] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = useCallback((s: StayMeals) => {
    setStay(s);
    setStayPref(s.stayPreference ?? '');
    setDayEdits({});
    setRemarks('');
  }, []);

  const load = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    try {
      reset(await mealEntitlementService.getStay(bookingId));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Could not read the stay\'s meals.');
    } finally {
      setLoading(false);
    }
  }, [bookingId, reset]);

  useEffect(() => { if (isOpen) { setStay(null); void load(); } }, [isOpen, load]);

  const planExpected = useMemo(
    () => stay?.pools.find((p) => p.planExpectedMeal)?.planExpectedMeal ?? null, [stay]);
  const fixedRule = useMemo(() => !!stay?.pools.some((p) => p.majorMealRule === 'FIXED'), [stay]);

  const prefChanged = !!stay && stayPref !== (stay.stayPreference ?? '');
  const changedDays = useMemo(() => {
    if (!stay) return [] as { day: StayMealDay; to: Choice }[];
    return stay.days
      .filter((d) => dayKey(d.date) in dayEdits && dayEdits[dayKey(d.date)] !== (d.dayChoice ?? ''))
      .map((d) => ({ day: d, to: dayEdits[dayKey(d.date)] }));
  }, [stay, dayEdits]);
  const isDirty = prefChanged || changedDays.length > 0;

  const handleClose = useCallback(() => { if (!saving) onClose(); }, [saving, onClose]);

  const save = useCallback(async () => {
    if (!stay || !bookingId) return;
    setConfirmOpen(false);
    setSaving(true);
    try {
      let latest = stay;
      if (prefChanged) latest = await mealEntitlementService.setPreference(bookingId, { majorMealPref: stayPref || null });
      for (const c of changedDays) {
        latest = await mealEntitlementService.setDay(bookingId, c.day.date, {
          majorMeal: c.to || null,
          remarks: c.to ? remarks.trim() || undefined : undefined,
        });
      }
      reset(latest);
      notificationService.toast.success('Meal choices saved.');
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The meal choices were not saved.');
      void load();
    } finally {
      setSaving(false);
    }
  }, [stay, bookingId, prefChanged, stayPref, changedDays, remarks, reset, load]);

  if (!isOpen) return null;

  const stayDefaultLabel = stayPref ? MEAL_LABEL[stayPref] : `Plan default (${planExpected ? MEAL_LABEL[planExpected] : 'Dinner'})`;

  const statusOf = (d: StayMealDay) => {
    const majors = d.allowances.filter((a) => a.allowance === 'MAJOR');
    const used = majors.reduce((s, a) => s + a.usedAdults + a.usedChildren, 0);
    if (majors.length === 0) return d.allowances.some((a) => a.allowance === 'BREAKFAST')
      ? <span className="badge badge-secondary">Breakfast only</span> : null;
    if (used > 0) {
      const at = majors.find((a) => a.usedAt)?.usedAt;
      return <span className="badge badge-success">Had {at ? MEAL_LABEL[at]?.toLowerCase() : 'the meal'} · {pax(
        majors.reduce((s, a) => s + a.usedAdults, 0), majors.reduce((s, a) => s + a.usedChildren, 0))}</span>;
    }
    if (d.isPast) return <span className="badge badge-secondary">Not used</span>;
    if (d.isToday) return <span className="badge badge-warning">Not used yet</span>;
    return <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Upcoming</span>;
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ position: 'relative' }}>
        {saving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', zIndex: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'inherit',
            fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500,
          }}>Saving…</div>
        )}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Meals · Room {stay?.pools.map((p) => p.rooms).join(', ') || roomNumbers || '—'}</h2>
          <button className="modal-close" onClick={handleClose}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          {loading && !stay && <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>}
          {stay && (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem 1.25rem', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                <span><b>{stay.guestName || 'Guest'}</b></span>
                <span>Booking {stay.bookingNumber}</span>
                <span>{fmtDate(stay.stayFrom)} → {fmtDate(stay.stayTo)}</span>
                <span className="badge badge-secondary" title="Local date-time minus the property's business-day cutoff">Business date {fmtDate(stay.businessDate)}</span>
              </div>

              {stay.pools.length === 0 ? (
                <div style={{ padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '6px', fontSize: '0.85rem' }}>
                  No meals are included in this stay's plan{stay.bookingStatusCode !== 'CHECKED_IN' ? ', or the stay is not checked in' : ''}.
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '0.75rem', fontSize: '0.82rem' }}>
                    {stay.pools.map((p) => (
                      <div key={p.poolKey}>
                        <b>{p.planCode}</b> {p.planName && <span style={{ color: 'var(--text-muted)' }}>({p.planName})</span>}
                        {' · '}{pax(p.adults, p.children)} · room {p.rooms}
                        {p.planExpectedMeal && (
                          <span style={{ color: 'var(--text-muted)' }}>
                            {' · '}one major meal a day, expected {MEAL_LABEL[p.planExpectedMeal].toLowerCase()}
                            {' · '}{p.majorMealRule === 'FIXED' ? 'only that meal counts' : 'guest\'s choice'}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>

                  {stay.hasMajorMealChoice && (
                    <div className="form-grid-2" style={{ marginBottom: '0.75rem' }}>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label">MAP meal the guests expect (whole stay)</label>
                        <select className="form-input" value={stayPref} disabled={!stay.canChangePreference || saving}
                          onChange={(e) => setStayPref(e.target.value as Choice)}>
                          <option value="">Plan default ({planExpected ? MEAL_LABEL[planExpected] : 'Dinner'})</option>
                          <option value="LUNCH">Lunch</option>
                          <option value="DINNER">Dinner</option>
                          <option value="EITHER">Either — not sure yet</option>
                        </select>
                      </div>
                      <div className="form-group" style={{ marginBottom: 0, display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                          {fixedRule
                            ? 'Only the expected meal counts as the plan meal; set a day below to let the guests take the other one.'
                            : 'It predicts the kitchen forecast and the restaurant follow-up. The guests may still take either meal.'}
                        </span>
                      </div>
                    </div>
                  )}

                  <div style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: '6px' }}>
                    <table className="table" style={{ width: '100%', fontSize: '0.82rem', margin: 0 }}>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Breakfast</th>
                          {stay.days.some((d) => d.allowances.some((a) => a.allowance === 'LUNCH')) && <th>Lunch</th>}
                          {stay.days.some((d) => d.allowances.some((a) => a.allowance === 'DINNER')) && <th>Dinner</th>}
                          {stay.hasMajorMealChoice && <th>MAP meal</th>}
                          {stay.hasMajorMealChoice && <th>Expected</th>}
                          {stay.hasMajorMealChoice && <th>Status</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {stay.days.map((d) => {
                          const of = (k: string) => d.allowances.filter((a) => a.allowance === k);
                          const majors = of('MAJOR');
                          const key = dayKey(d.date);
                          const value: Choice = key in dayEdits ? dayEdits[key] : (d.dayChoice ?? '');
                          const expected = majors[0]?.expected;
                          return (
                            <tr key={key} style={{ background: d.isToday ? 'var(--bg-secondary)' : undefined, color: d.isPast ? 'var(--text-muted)' : undefined }}>
                              <td style={{ whiteSpace: 'nowrap' }}><b>{fmtDay(d.date)}</b>{d.isToday && <span className="badge badge-info" style={{ marginLeft: '0.35rem', fontSize: '0.62rem' }}>Today</span>}</td>
                              <td>{allowanceText(of('BREAKFAST')) ?? '—'}</td>
                              {stay.days.some((x) => x.allowances.some((a) => a.allowance === 'LUNCH')) && <td>{allowanceText(of('LUNCH')) ?? '—'}</td>}
                              {stay.days.some((x) => x.allowances.some((a) => a.allowance === 'DINNER')) && <td>{allowanceText(of('DINNER')) ?? '—'}</td>}
                              {stay.hasMajorMealChoice && (
                                <td>{majors.length ? `${allowanceText(majors)} · ${majors[0].meals.length > 1 ? 'L or D' : MEAL_LABEL[majors[0].meals[0]]}` : '—'}</td>
                              )}
                              {stay.hasMajorMealChoice && (
                                <td>
                                  {majors.length === 0 ? '—' : d.canChangeChoice ? (
                                    <select className="form-input" style={{ padding: '0.2rem 0.4rem', fontSize: '0.8rem', minWidth: 140 }}
                                      value={value} disabled={saving}
                                      onChange={(e) => setDayEdits((prev) => ({ ...prev, [key]: e.target.value as Choice }))}>
                                      <option value="">Stay ({stayDefaultLabel})</option>
                                      <option value="LUNCH">Lunch</option>
                                      <option value="DINNER">Dinner</option>
                                      <option value="EITHER">Either</option>
                                    </select>
                                  ) : (
                                    <span title={d.dayChoiceRemarks ?? undefined}>
                                      {expected ? MEAL_LABEL[expected] : '—'}
                                      {majors[0]?.expectedFrom === 'DAY' && <span style={{ color: 'var(--text-muted)' }}> (this day)</span>}
                                    </span>
                                  )}
                                </td>
                              )}
                              {stay.hasMajorMealChoice && <td>{statusOf(d)}</td>}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {changedDays.some((c) => c.to) && (
                    <div className="form-group" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                      <label className="form-label">Reason for the day's change (optional)</label>
                      <input type="text" className="form-input" maxLength={500} value={remarks} disabled={saving}
                        placeholder="e.g. Out sightseeing until evening" onChange={(e) => setRemarks(e.target.value)} />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Close</button>
          {stay?.canChangePreference && (
            <button className="btn btn-primary" onClick={() => setConfirmOpen(true)} disabled={!isDirty || saving}
              title={!isDirty ? 'No changes to save' : undefined}>Save changes</button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Save meal choices"
        message={[
          prefChanged ? `Whole stay: ${stayPref ? MEAL_LABEL[stayPref] : 'plan default'}.` : null,
          ...changedDays.map((c) => `${fmtDay(c.day.date)}: ${c.to ? MEAL_LABEL[c.to] : 'back to the stay'}.`),
        ].filter(Boolean).join(' ')}
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={() => { void save(); }}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default StayMealsModal;
